
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { UserPlus, Users } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { useJournalistFollow } from '@/hooks/journalist';
import { Journalist } from '@/components/reader-home/types';

const AllAuthors = () => {
  const [authors, setAuthors] = useState<Journalist[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState<Record<string, boolean>>({});
  const { toggleFollow } = useJournalistFollow();

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        setLoading(true);
        
        // Get all journalists (users with role 'journalist')
        const { data: journalists, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_role', 'journalist');
          
        if (error) throw error;
        
        // Get current user's followed journalists
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: follows, error: followsError } = await supabase
            .from('journalist_follows')
            .select('journalist_id')
            .eq('user_id', user.id);
            
          if (followsError) throw followsError;
          
          // Create a map of journalist IDs to following status
          const followMap: Record<string, boolean> = {};
          if (follows) {
            follows.forEach(follow => {
              followMap[follow.journalist_id] = true;
            });
          }
          setFollowingMap(followMap);
        }
        
        // Map the data to our Journalist format
        const formattedJournalists: Journalist[] = journalists?.map(profile => ({
          id: profile.id,
          name: profile.display_name || 'Unknown',
          avatar: profile.avatar_url || '',
          bio: profile.bio || '',
          followers: 0, // We could fetch this separately if needed
          topics: [], // We could add this later
          unread: 0 // We could implement this with a notification system
        })) || [];
        
        setAuthors(formattedJournalists);
      } catch (err) {
        console.error('Error fetching authors:', err);
        toast({
          title: 'Error',
          description: 'Failed to load authors',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, []);

  const handleToggleFollow = async (journalistId: string) => {
    try {
      await toggleFollow(journalistId);
      
      // Update the local following state
      setFollowingMap(prev => ({
        ...prev,
        [journalistId]: !prev[journalistId]
      }));
      
      const action = followingMap[journalistId] ? 'unfollowed' : 'followed';
      const journalist = authors.find(a => a.id === journalistId);
      
      toast({
        title: `${action === 'followed' ? 'Following' : 'Unfollowed'}`,
        description: `You have ${action} ${journalist?.name || 'this journalist'}`,
      });
    } catch (err) {
      console.error('Error toggling follow:', err);
      toast({
        title: 'Action failed',
        description: 'There was a problem updating your follow status',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center gap-2 mb-6">
          <Users className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Authors</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((_, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="flex items-center gap-2 mb-6">
        <Users className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Authors</h1>
      </div>
      
      {authors.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No authors found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {authors.map(author => (
            <Card key={author.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Link to={`/journalist/${author.id}`} className="flex items-center gap-4 flex-1">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={author.avatar} alt={author.name} />
                      <AvatarFallback>{author.name ? author.name.substring(0, 2) : 'JN'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-medium">{author.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{author.bio || 'No bio available'}</p>
                    </div>
                  </Link>
                  <Button
                    variant={followingMap[author.id] ? "default" : "outline"}
                    size="sm"
                    className="ml-2 flex-shrink-0"
                    onClick={() => handleToggleFollow(author.id)}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    {followingMap[author.id] ? 'Following' : 'Follow'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllAuthors;
